import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, updateDoc, doc } from 'firebase/firestore';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import { Users, UserCheck, TrendingDown, Clock, Hourglass, Star } from 'lucide-react';
import { db } from '../firebase';
import BBALoader from '../components/BBALoader';
import { PinnableCard, ReportBackdrop, useReportCardMode } from '../components/crm/reportCards';
import { useTheme } from '../contexts/ThemeContext';
import { resolveClientLifecycle, getProgramTier, isActiveClientAccount, programMapWith } from '../utils/accountLifecycle';
import {
  GLASS_PALETTE,
  GLASS_TONE,
  GLASS_TEXT,
  GLASS_FIELD,
  GLASS_TH,
  GLASS_ROW,
  GLASS_INSET,
  GLASS_MODAL_OVERLAY,
  GLASS_MODAL_PANEL,
  GLASS_SECONDARY_BUTTON,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassFigure,
  GlassChip,
  GlassBadge,
  GlassAccentButton,
  GlassAvatar,
  GlassTooltip,
  GlassEmpty,
  GlassDonut,
  GlassBreakdownCard,
  SlidingPills,
  glassChartTheme,
} from '../components/glass/GlassUi';

const colors = GLASS_PALETTE.slice(0, 8);

const TAB_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'Active', label: 'Active' },
  { value: 'Lost', label: 'Lost' },
];

const TIME_OPTIONS = [
  { value: 'All Time', label: 'All time' },
  { value: 'This Year', label: 'Year' },
  { value: 'This Quarter', label: 'Quarter' },
  { value: 'This Month', label: 'Month' },
];

const PERIOD_PHRASE = { 'All Time': 'on record', 'This Year': 'this year', 'This Quarter': 'this quarter', 'This Month': 'this month' };

const PROGRAM_STAGES = ['Lite', 'Plus', 'Pro', 'Hourly', '5 Hours', '10 Hours', 'A La Carte'];

// Department star ratings, laid out 3 then 4 like the original rows
const RATING_ROWS = [
  [
    { name: 'General', key: 'general' },
    { name: 'Sales', key: 'sales' },
    { name: 'Operations', key: 'operations' },
  ],
  [
    { name: 'Technology', key: 'technology' },
    { name: 'Finance', key: 'finance' },
    { name: 'Marketing', key: 'marketing' },
    { name: 'HR', key: 'hr' },
  ],
];

const MetricTile = ({ label, value, tone = 'strong' }) => (
  <div className={`${GLASS_INSET} px-4 py-4`}>
    <div className="text-[11px] text-gray-500 dark:text-white/[0.45]">{label}</div>
    <div className={`mt-1 text-2xl font-semibold tabular-nums ${GLASS_TEXT[tone]}`}>{value}</div>
  </div>
);

const RatingTile = ({ name, rating }) => (
  <div className={`${GLASS_INSET} flex h-32 flex-col items-center justify-center p-4 text-center`}>
    <div className="mb-3 text-sm font-medium text-gray-700 dark:text-white/75">{name}</div>
    <div className="mb-2 flex items-center justify-center gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <Star
          key={star}
          className={`h-5 w-5 ${star <= Math.round(rating) ? 'text-[#FFB547]' : 'text-black/10 dark:text-white/10'}`}
          fill="currentColor"
          strokeWidth={0}
        />
      ))}
    </div>
    {rating > 0 && (
      <div className="text-sm font-semibold text-gray-900 tabular-nums dark:text-white">
        {rating.toFixed(1)}
      </div>
    )}
  </div>
);

// One tab's header + cards. The page renders one view for the selected tab; My view renders
// every tab at once (without the header) so a card pinned from any tab shows up.
const ClientsTabView = ({ activeTab, setActiveTab, allClients, timeFilter, setTimeFilter, showHeader = true }) => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  const filterByTime = (items, dateField) => {
    if (!items?.length) return [];
    if (timeFilter === 'All Time') return items;
    const now = new Date();
    const start = new Date();
    if (timeFilter === 'This Year') {
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
    } else if (timeFilter === 'This Quarter') {
      const q = Math.floor(now.getMonth() / 3);
      start.setMonth(q * 3, 1);
      start.setHours(0, 0, 0, 0);
    } else if (timeFilter === 'This Month') {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
    }
    return items.filter(it => {
      const dt = it[dateField];
      return dt instanceof Date && dt >= start;
    });
  };

  // Filter clients based on active tab
  const clients = useMemo(() => {
    // One lifecycle rule (accountLifecycle.js): Lost / Canceled / Archived — stored, or derived
    // from a cancel date that has passed — count as lost here; everything else is active.
    if (activeTab === 'Lost') {
      return allClients.filter((c) => resolveClientLifecycle(c).isEnded);
    }
    if (activeTab === 'Active') {
      return allClients.filter((c) => isActiveClientAccount(c));
    }
    return allClients;
  }, [allClients, activeTab]);

  const filteredClients = useMemo(() => {
    // Lost tab: filter by when the client actually ended (lostAt / canceledAt / archivedAt, or the
    // cancel date that passed), falling back to createdAt for docs written before those fields.
    if (activeTab === 'Lost') {
      const withEnded = clients.map((c) => ({ ...c, endedAt: resolveClientLifecycle(c).endedAt || c.lostAt || c.createdAt }));
      return filterByTime(withEnded, 'endedAt');
    }
    return filterByTime(clients, 'createdAt');
  }, [clients, timeFilter, activeTab]);

  const totals = useMemo(() => {
    if (activeTab === 'Lost') {
      return {
        total: filteredClients.length,
        lost: filteredClients.length,
      };
    }
    if (activeTab === 'Active') {
      return {
        total: filteredClients.length,
        active: filteredClients.length,
      };
    }
    // For All tab
    const lost = filteredClients.filter(c => {
      const status = c.status || c.program?.clientStatus || c.stage;
      return status === 'Lost' || status === 'lost';
    }).length;
    const active = filteredClients.filter(c => {
      const status = c.status || c.program?.clientStatus || c.stage;
      return status !== 'Lost' && status !== 'lost';
    }).length;
    return {
      total: filteredClients.length,
      lost,
      active,
    };
  }, [filteredClients, activeTab]);

  const lostByReason = useMemo(() => {
    const counts = {};
    filteredClients.forEach(c => {
      const reason = c.resultReason || c.substatus || c.reason || c.lostReason || 'Unknown';
      counts[reason] = (counts[reason] || 0) + 1;
    });
    // Filter out Unknown entries if there are actual reasons, otherwise show Unknown
    const entries = Object.entries(counts);
    const hasActualReasons = entries.some(([reason]) => reason !== 'Unknown' && counts[reason] > 0);
    const filtered = hasActualReasons 
      ? entries.filter(([reason]) => reason !== 'Unknown')
      : entries;
    return filtered.map(([reason, count], idx) => ({ reason, count, color: colors[idx % colors.length] }));
  }, [filteredClients]);

  const lostByStage = useMemo(() => {
    // Client stages: Lite, Plus, Pro, Hourly, 5 Hours, 10 Hours, A La Carte
    const STAGES = ['Lite', 'Plus', 'Pro', 'Hourly', '5 Hours', '10 Hours', 'A La Carte'];
    const counts = STAGES.reduce((acc, s) => { acc[s] = 0; return acc; }, {});
    filteredClients.forEach(c => {
      const previousStage = c.program?.programName
        || c.program?.status
        || c.lastVisitedStage
        || c.previousStage
        || (Array.isArray(c.stageHistory) && c.stageHistory.length > 1 ? c.stageHistory[c.stageHistory.length - 2]?.stage : null)
        || c.stageBeforeLost
        || c.pipelineStage
        || null;
      if (previousStage && STAGES.includes(previousStage)) {
        counts[previousStage] = (counts[previousStage] || 0) + 1;
      }
    });
    // Only return stages that have counts > 0
    return STAGES
      .filter(stage => counts[stage] > 0)
      .map((stage, idx) => ({ stage, count: counts[stage] || 0, color: colors[idx % colors.length] }));
  }, [filteredClients]);

  const longTermLostClients = useMemo(() => {
    if (!filteredClients.length) return 0;
    // Count clients with program duration >= 6 months (180 days)
    const longTermCount = filteredClients.filter(c => {
      // Use program start and end dates from program overview
      const programStart = c.program?.startDate;
      const programEnd = c.program?.endDate;
      const start = programStart 
        ? (programStart instanceof Date ? programStart : new Date(programStart))
        : null;
      const end = programEnd 
        ? (programEnd instanceof Date ? programEnd : new Date(programEnd))
        : null;
      if (!start || !end || !(start instanceof Date) || !(end instanceof Date) || isNaN(start.getTime()) || isNaN(end.getTime())) return false;
      const days = Math.round((end - start) / (1000 * 60 * 60 * 24));
      return days >= 180; // 6 months or more
    }).length;
    return longTermCount;
  }, [filteredClients]);

  const avgDaysAsClient = useMemo(() => {
    if (!filteredClients.length) return 0;
    const days = filteredClients.map(c => {
      // Use program start and end dates from program overview
      const programStart = c.program?.startDate;
      const programEnd = c.program?.endDate;
      const start = programStart 
        ? (programStart instanceof Date ? programStart : new Date(programStart))
        : null;
      const end = programEnd 
        ? (programEnd instanceof Date ? programEnd : new Date(programEnd))
        : null;
      if (!start || !end || !(start instanceof Date) || !(end instanceof Date) || isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
      return Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
    }).filter(d => d > 0); // Only include valid day calculations
    if (days.length === 0) return 0;
    const sum = days.reduce((a, b) => a + b, 0);
    return Math.round((sum / days.length) * 10) / 10;
  }, [filteredClients]);

  const lostClientsOverTime = useMemo(() => {
    if (!filteredClients.length) return [];
    const periodCounts = {};
    const now = new Date();
    
    // Determine the start date based on time filter
    let periodStart = new Date(0); // Beginning of time for "All Time"
    if (timeFilter === 'This Year') {
      periodStart = new Date(now.getFullYear(), 0, 1);
    } else if (timeFilter === 'This Quarter') {
      const q = Math.floor(now.getMonth() / 3);
      periodStart = new Date(now.getFullYear(), q * 3, 1);
    } else if (timeFilter === 'This Month') {
      periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    
    filteredClients.forEach(c => {
      // Use program end date, lostAt, or lastActivityAt to determine when they were lost
      const programEnd = c.program?.endDate;
      const lostDate = programEnd 
        ? (programEnd instanceof Date ? programEnd : new Date(programEnd))
        : (c.lostAt instanceof Date ? c.lostAt : (c.lostAt?.toDate ? c.lostAt.toDate() : null)) 
          || (c.lastActivityAt instanceof Date ? c.lastActivityAt : (c.lastActivityAt?.toDate ? c.lastActivityAt.toDate() : null));
      
      if (lostDate && lostDate instanceof Date && !isNaN(lostDate.getTime())) {
        let periodKey;
        let label;
        let dateForSort;
        
        if (timeFilter === 'All Time') {
          // Group by year
          const year = lostDate.getFullYear();
          periodKey = `${year}`;
          label = year.toString();
          dateForSort = new Date(year, 0, 1);
        } else if (timeFilter === 'This Year') {
          // Group by month
          const month = lostDate.getMonth();
          periodKey = `${lostDate.getFullYear()}-${String(month + 1).padStart(2, '0')}`;
          label = lostDate.toLocaleDateString('en-US', { month: 'short' });
          dateForSort = new Date(lostDate.getFullYear(), month, 1);
        } else if (timeFilter === 'This Quarter') {
          // Group by month in quarter
          const month = lostDate.getMonth();
          periodKey = `${lostDate.getFullYear()}-${String(month + 1).padStart(2, '0')}`;
          label = lostDate.toLocaleDateString('en-US', { month: 'short' });
          dateForSort = new Date(lostDate.getFullYear(), month, 1);
        } else if (timeFilter === 'This Month') {
          // Group by week
          const weekStart = new Date(lostDate);
          weekStart.setDate(lostDate.getDate() - lostDate.getDay()); // Start of week (Sunday)
          weekStart.setHours(0, 0, 0, 0);
          // Calculate week number within the month (1st week, 2nd week, etc.)
          const firstDayOfMonth = new Date(lostDate.getFullYear(), lostDate.getMonth(), 1);
          const firstWeekStart = new Date(firstDayOfMonth);
          firstWeekStart.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());
          const weekNum = Math.floor((weekStart - firstWeekStart) / (7 * 24 * 60 * 60 * 1000)) + 1;
          periodKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
          label = `Week ${weekNum} (${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
          dateForSort = new Date(weekStart);
        }
        
        if (periodKey && (!periodStart || lostDate >= periodStart)) {
          if (!periodCounts[periodKey]) {
            periodCounts[periodKey] = { count: 0, label, dateForSort };
          }
          periodCounts[periodKey].count++;
        }
      }
    });
    
    // Convert to array and sort by date
    const sorted = Object.entries(periodCounts)
      .map(([key, { count, label, dateForSort }]) => ({
        period: label,
        date: dateForSort,
        count
      }))
      .sort((a, b) => a.date - b.date);
    
    // For "This Year" and "This Quarter", ensure all months are included even if count is 0
    if (timeFilter === 'This Year' || timeFilter === 'This Quarter') {
      const startDate = timeFilter === 'This Year' 
        ? new Date(now.getFullYear(), 0, 1)
        : new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      const endDate = now;
      const months = [];
      const current = new Date(startDate);
      
      while (current <= endDate) {
        const monthKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
        const existing = sorted.find(s => s.date.getTime() === current.getTime());
        months.push({
          period: current.toLocaleDateString('en-US', { month: 'short' }),
          date: new Date(current),
          count: existing ? existing.count : 0
        });
        current.setMonth(current.getMonth() + 1);
      }
      
      return months;
    }
    
    // For "This Month", ensure all weeks are included
    if (timeFilter === 'This Month') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const weeks = [];
      let weekStart = new Date(monthStart);
      weekStart.setDate(monthStart.getDate() - monthStart.getDay()); // Start of first week
      weekStart.setHours(0, 0, 0, 0);
      const firstWeekStart = new Date(weekStart);
      let weekNum = 1;
      
      while (weekStart <= monthEnd) {
        const weekKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
        const existing = sorted.find(s => {
          const sWeekStart = new Date(s.date);
          sWeekStart.setHours(0, 0, 0, 0);
          return sWeekStart.getTime() === weekStart.getTime();
        });
        
        if (weekStart >= monthStart || (weekStart < monthStart && weekStart.getDate() + 6 >= monthStart.getDate())) {
          weeks.push({
            period: `Week ${weekNum} (${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`,
            date: new Date(weekStart),
            count: existing ? existing.count : 0
          });
        }
        
        weekStart.setDate(weekStart.getDate() + 7);
        weekNum++;
      }
      
      return weeks;
    }
    
    return sorted;
  }, [filteredClients, timeFilter]);

  // Calculate stage distribution for Active tab
  const stageDistribution = useMemo(() => {
    const STAGES = ['Lite', 'Plus', 'Pro', 'Hourly', '5 Hours', '10 Hours', 'A La Carte'];
    const counts = STAGES.reduce((acc, s) => { acc[s] = 0; return acc; }, {});
    filteredClients.forEach(c => {
      const stage = c.program?.programName || c.program?.status || c.stage || 'Unknown';
      if (STAGES.includes(stage)) {
        counts[stage] = (counts[stage] || 0) + 1;
      }
    });
    return counts;
  }, [filteredClients]);

  // Calculate revenue metrics
  const revenueMetrics = useMemo(() => {
    let startingRevenue = 0;
    let currentRevenue = 0;
    let revenueGoal = 0;
    let startingCount = 0;
    let currentCount = 0;
    let goalCount = 0;

    filteredClients.forEach(c => {
      const startRev = c.metrics?.startingRevenue || 0;
      const currRev = c.metrics?.currentRevenue || 0;
      const goalRev = c.metrics?.revenueGoal || 0;
      
      if (startRev > 0) {
        startingRevenue += startRev;
        startingCount++;
      }
      if (currRev > 0) {
        currentRevenue += currRev;
        currentCount++;
      }
      if (goalRev > 0) {
        revenueGoal += goalRev;
        goalCount++;
      }
    });

    return {
      startingRevenue: startingRevenue,
      currentRevenue: currentRevenue,
      revenueGoal: revenueGoal,
      avgStartingRevenue: startingCount > 0 ? Math.round(startingRevenue / startingCount) : 0,
      avgCurrentRevenue: currentCount > 0 ? Math.round(currentRevenue / currentCount) : 0,
      avgRevenueGoal: goalCount > 0 ? Math.round(revenueGoal / goalCount) : 0,
      startingCount,
      currentCount,
      goalCount
    };
  }, [filteredClients]);

  // Calculate average stars for departments
  const avgStars = useMemo(() => {
    const departments = ['general', 'sales', 'operations', 'technology', 'finance', 'marketing', 'hr'];
    const stars = {};
    
    departments.forEach(dept => {
      const ratings = [];
      filteredClients.forEach(c => {
        const rating = c.departments?.[dept]?.rating;
        if (rating && typeof rating === 'number' && rating > 0) {
          ratings.push(rating);
        }
      });
      stars[dept] = ratings.length > 0 
        ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10
        : 0;
    });

    return stars;
  }, [filteredClients]);

  // Calculate date metrics
  const dateMetrics = useMemo(() => {
    let totalDays = 0;
    let validClients = 0;
    const now = new Date();

    filteredClients.forEach(c => {
      const programStart = c.program?.startDate;
      const programEnd = c.program?.endDate;
      const start = programStart 
        ? (programStart instanceof Date ? programStart : new Date(programStart))
        : null;
      const end = programEnd 
        ? (programEnd instanceof Date ? programEnd : new Date(programEnd))
        : (c.status === 'Lost' || c.status === 'lost' ? null : now); // Use now for active clients
      
      if (start && start instanceof Date && !isNaN(start.getTime())) {
        const endDate = end && end instanceof Date && !isNaN(end.getTime()) ? end : now;
        const days = Math.round((endDate - start) / (1000 * 60 * 60 * 24));
        if (days >= 0) {
          totalDays += days;
          validClients++;
        }
      }
    });

    return {
      avgClientLength: validClients > 0 ? Math.round((totalDays / validClients) * 10) / 10 : 0,
      validClients
    };
  }, [filteredClients]);

  // Calculate hour metrics
  const hourMetrics = useMemo(() => {
    let totalHourPlans = 0;
    let totalHoursUsed = 0;
    let clientsWithHourData = 0;

    filteredClients.forEach(c => {
      const hourlyPlan = c.hourlyPlan || 0;
      const timeEntries = c.timeEntries || [];
      
      if (hourlyPlan > 0 || timeEntries.length > 0) {
        totalHourPlans += hourlyPlan;
        
        // Calculate total hours from time entries
        const hoursFromEntries = timeEntries.reduce((total, entry) => {
          const hours = entry.hours || 0;
          const minutes = entry.minutes || 0;
          return total + hours + (minutes / 60);
        }, 0);
        
        totalHoursUsed += hoursFromEntries;
        clientsWithHourData++;
      }
    });

    return {
      totalHourPlans,
      totalHoursUsed: Math.round(totalHoursUsed * 10) / 10,
      clientsWithHourData
    };
  }, [filteredClients]);

  // Get status counts for All tab
  const statusCounts = useMemo(() => {
    if (activeTab !== 'All') return { active: 0, lost: 0, total: 0 };
    
    let active = 0;
    let lost = 0;
    
    filteredClients.forEach(c => {
      const status = c.status || c.program?.clientStatus || c.stage;
      if (status === 'Lost' || status === 'lost') {
        lost++;
      } else {
        active++;
      }
    });

    return { active, lost, total: filteredClients.length };
  }, [filteredClients, activeTab]);

  // Calculate clients over time for line graph
  const clientsOverTime = useMemo(() => {
    if (!filteredClients.length && activeTab !== 'Lost') return [];
    const now = new Date();
    
    // Determine the start date and periods based on time filter
    let periodStart = new Date(0);
    let periods = [];
    
    if (timeFilter === 'All Time') {
      // Get all years from first client to now
      const years = new Set();
      filteredClients.forEach(c => {
        const created = c.createdAt instanceof Date ? c.createdAt : (c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt));
        if (created && created instanceof Date && !isNaN(created.getTime())) {
          years.add(created.getFullYear());
        }
      });
      const currentYear = now.getFullYear();
      years.add(currentYear);
      periods = Array.from(years).sort().map(year => ({
        key: `${year}`,
        label: year.toString(),
        date: new Date(year, 0, 1),
        endDate: new Date(year, 11, 31, 23, 59, 59)
      }));
    } else if (timeFilter === 'This Year') {
      const startDate = new Date(now.getFullYear(), 0, 1);
      const current = new Date(startDate);
      while (current <= now) {
        const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
        const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0, 23, 59, 59);
        periods.push({
          key: `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`,
          label: current.toLocaleDateString('en-US', { month: 'short' }),
          date: monthStart,
          endDate: monthEnd
        });
        current.setMonth(current.getMonth() + 1);
      }
    } else if (timeFilter === 'This Quarter') {
      const q = Math.floor(now.getMonth() / 3);
      const startDate = new Date(now.getFullYear(), q * 3, 1);
      const current = new Date(startDate);
      while (current <= now) {
        const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
        const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0, 23, 59, 59);
        periods.push({
          key: `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`,
          label: current.toLocaleDateString('en-US', { month: 'short' }),
          date: monthStart,
          endDate: monthEnd
        });
        current.setMonth(current.getMonth() + 1);
      }
    } else if (timeFilter === 'This Month') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      let weekStart = new Date(monthStart);
      weekStart.setDate(monthStart.getDate() - monthStart.getDay());
      weekStart.setHours(0, 0, 0, 0);
      let weekNum = 1;
      const firstWeekStart = new Date(weekStart);
      
      while (weekStart <= monthEnd) {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59);
        
        if (weekStart >= monthStart || (weekStart < monthStart && weekStart.getDate() + 6 >= monthStart.getDate())) {
          const weekNumCalc = Math.floor((weekStart - firstWeekStart) / (7 * 24 * 60 * 60 * 1000)) + 1;
          periods.push({
            key: `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`,
            label: `Week ${weekNumCalc} (${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`,
            date: new Date(weekStart),
            endDate: weekEnd
          });
        }
        weekStart.setDate(weekStart.getDate() + 7);
        weekNum++;
      }
    }
    
    // For Lost tab, count when clients were lost (existing logic)
    if (activeTab === 'Lost') {
      const periodCounts = {};
      filteredClients.forEach(c => {
        const programEnd = c.program?.endDate;
        const lostDate = programEnd 
          ? (programEnd instanceof Date ? programEnd : (programEnd === 'N/A' || programEnd === null || programEnd === undefined ? null : new Date(programEnd)))
          : (c.lostAt instanceof Date ? c.lostAt : (c.lostAt?.toDate ? c.lostAt.toDate() : null)) 
            || (c.lastActivityAt instanceof Date ? c.lastActivityAt : (c.lastActivityAt?.toDate ? c.lastActivityAt.toDate() : null));
        
        if (!lostDate || !(lostDate instanceof Date) || isNaN(lostDate.getTime())) return;
        
        // Find which period this belongs to
        const period = periods.find(p => lostDate >= p.date && lostDate <= p.endDate);
        if (period) {
          if (!periodCounts[period.key]) {
            periodCounts[period.key] = 0;
          }
          periodCounts[period.key]++;
        }
      });
      
      return periods.map(p => ({
        period: p.label,
        date: p.date,
        count: periodCounts[p.key] || 0
      }));
    }
    
    // For Active/All tabs, calculate cumulative active (non-lost) clients at each period
    // Use allClients for All tab to get all clients, filteredClients for Active tab
    const clientsToCheck = activeTab === 'All' ? allClients : filteredClients;
    
    return periods.map(period => {
      const count = clientsToCheck.filter(c => {
        const created = c.createdAt instanceof Date ? c.createdAt : (c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt));
        if (!created || !(created instanceof Date) || isNaN(created.getTime())) return false;
        
        // Client must have been created before or during this period
        if (created > period.endDate) return false;
        
        // Check if client was still active (non-lost) at the end of this period
        const status = c.status || c.program?.clientStatus || c.stage;
        const programEnd = c.program?.endDate;
        const endDate = programEnd 
          ? (programEnd instanceof Date ? programEnd : (programEnd === 'N/A' || programEnd === null || programEnd === undefined ? null : new Date(programEnd)))
          : null;
        
        const isLost = status === 'Lost' || status === 'lost';
        const hasNoEndDate = endDate === null || endDate === undefined || endDate === 'N/A';
        
        // Client is active at period end if:
        // 1. Not lost, OR
        // 2. Lost but end date is after period end (was still active during this period), OR
        // 3. Lost but no end date (treat as active if status allows)
        if (isLost) {
          if (hasNoEndDate) {
            // If lost but no end date, don't count as active
            return false;
          }
          if (endDate && endDate instanceof Date && !isNaN(endDate.getTime())) {
            // If lost with end date, only count if end date is after period end
            return endDate > period.endDate;
          }
          return false;
        }
        
        // Not lost - check if they have an end date that would exclude them
        if (!hasNoEndDate && endDate && endDate instanceof Date && !isNaN(endDate.getTime())) {
          // If end date exists and is before/at period end, they weren't active
          return endDate > period.endDate;
        }
        
        // Client is active
        return true;
      }).length;
      
      return {
        period: period.label,
        date: period.date,
        count
      };
    });
  }, [filteredClients, allClients, timeFilter, activeTab]);

  const chart = glassChartTheme(darkMode);
  const pctOf = (n, total) => (total ? Math.round((n / total) * 100) : 0);
  const allLostCount = allClients.filter(c => {
    const status = c.status || c.program?.clientStatus || c.stage;
    return status === 'Lost' || status === 'lost';
  }).length;

  const stats = activeTab === 'Lost' ? [
    { icon: TrendingDown, value: totals.total, label: 'Total Lost Clients' },
    { icon: Hourglass, value: longTermLostClients, label: 'Long Term (6 Months+) Lost Clients' },
    { icon: Clock, value: avgDaysAsClient, label: 'Avg Days As Client' },
  ] : activeTab === 'Active' ? [
    { icon: UserCheck, value: totals.total, label: 'Active Clients', valueClassName: GLASS_TEXT.mint },
    { icon: Users, value: allClients.length, label: 'All Clients' },
    { icon: TrendingDown, value: allLostCount, label: 'Lost Clients', valueClassName: GLASS_TEXT.red },
    { icon: Clock, value: dateMetrics.avgClientLength, label: 'Avg Client Length (Days)' },
  ] : [
    { icon: Users, value: totals.total, label: 'Total Clients' },
    { icon: UserCheck, value: statusCounts.active, label: 'Active Clients', badge: `${pctOf(statusCounts.active, statusCounts.total)}%`, badgeTone: 'mint' },
    { icon: TrendingDown, value: statusCounts.lost, label: 'Lost Clients', badge: `${pctOf(statusCounts.lost, statusCounts.total)}%`, badgeTone: 'red' },
    { icon: Clock, value: dateMetrics.avgClientLength, label: 'Avg Client Length (Days)' },
  ];

  const overTimeColor = activeTab === 'Lost' ? GLASS_TONE.red : activeTab === 'Active' ? GLASS_TONE.mint : GLASS_TONE.blue;
  const overTimeTitle = activeTab === 'Lost' ? 'Lost Clients Over Time' : activeTab === 'Active' ? 'Active Clients Over Time' : 'Clients Over Time';
  const overTimeName = activeTab === 'Lost' ? 'Lost Clients' : activeTab === 'Active' ? 'Active Clients' : 'Clients';
  // Lost buckets are counts per period; Active / All buckets are a running active total
  const overTimeFigure = activeTab === 'Lost'
    ? clientsOverTime.reduce((sum, p) => sum + p.count, 0)
    : (clientsOverTime.length ? clientsOverTime[clientsOverTime.length - 1].count : 0);
  const overTimeCaption = activeTab === 'Lost'
    ? `lost clients, ${PERIOD_PHRASE[timeFilter] || ''}`
    : 'active clients, latest period';
  // Week buckets read "Week 2 (Nov 9)"; the axis keeps just "Week 2", the tooltip keeps the date
  const shortPeriod = (v) => String(v).replace(/\s*\(.*\)$/, '');

  const stageData = PROGRAM_STAGES
    .map(stage => ({ stage, count: stageDistribution[stage] || 0 }))
    .filter(item => item.count > 0)
    .map((item, idx) => ({ ...item, color: colors[idx % colors.length] }));
  const stageTotal = stageData.reduce((sum, s) => sum + s.count, 0);

  const muted = 'text-gray-500 dark:text-white/50';
  // Card ids / titles are per tab: My view renders every tab, so each tab's cards pin separately
  const cardPrefix = activeTab.toLowerCase();
  const tabLabel = activeTab === 'All' ? 'All Clients' : `${activeTab} Clients`;
  const overTimeFillId = `clientsOverTimeFill-${cardPrefix}`;
  const companyCell = (c) => {
    const companyName = c.company || c.accountName || c.businessName || c.organization || c.name || c.email || c.phone || c.id;
    return (
      <div className="flex items-center gap-3">
        <GlassAvatar name={companyName} />
        <span className="font-medium text-gray-900 dark:text-white">{companyName}</span>
      </div>
    );
  };

  const overTimeCard = (
    <PinnableCard id={`${cardPrefix}-over-time`} title={overTimeTitle} size="wide">
    <GlassCard className={`p-5 ${activeTab === 'Lost' ? '' : 'xl:col-span-2'}`} delay={0}>
      <GlassCardHeader title={overTimeTitle} />
      <GlassFigure className="mt-4" value={overTimeFigure.toLocaleString()} caption={overTimeCaption} />
      <div className="mt-4 flex flex-wrap gap-2">
        {activeTab === 'Lost' ? (
          <>
            <GlassChip color={GLASS_TONE.red} label="Lost" value={totals.total} />
            <GlassChip color={GLASS_TONE.amber} label="Long term (6 mo+)" value={longTermLostClients} />
          </>
        ) : activeTab === 'Active' ? (
          <>
            <GlassChip color={GLASS_TONE.mint} label="Active" value={totals.total} />
            <GlassChip color={GLASS_TONE.red} label="Lost" value={allLostCount} />
          </>
        ) : (
          <>
            <GlassChip color={GLASS_TONE.mint} label="Active" value={statusCounts.active} />
            <GlassChip color={GLASS_TONE.red} label="Lost" value={statusCounts.lost} />
          </>
        )}
      </div>
      <div className="mt-4 h-64">
        {clientsOverTime.length === 0 ? (
          <GlassEmpty className="h-full">No client data available</GlassEmpty>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={clientsOverTime} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id={overTimeFillId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={overTimeColor} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={overTimeColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke={chart.grid} strokeDasharray="4 6" />
              <XAxis
                dataKey="period"
                tickFormatter={shortPeriod}
                tick={{ fill: chart.tick, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={8}
              />
              <YAxis allowDecimals={false} tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
              <Tooltip cursor={{ stroke: chart.grid, strokeWidth: 1 }} content={<GlassTooltip unit="clients" />} />
              <Area
                type="monotone"
                dataKey="count"
                name={overTimeName}
                stroke={overTimeColor}
                strokeWidth={2.5}
                fill={`url(#${overTimeFillId})`}
                dot={false}
                activeDot={{ r: 5, strokeWidth: 0, fill: overTimeColor }}
                animationDuration={700}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </GlassCard>
    </PinnableCard>
  );

  return (
    <>
      {showHeader && (
        <GlassPageHeader
          backTo="/crm/reports"
          backTitle="Back to reports"
          center={<SlidingPills options={TAB_OPTIONS} value={activeTab} onChange={setActiveTab} ariaLabel="Client status" />}
          title={<>Clients<br />Reports</>}
          stats={stats}
          statsKey={activeTab}
          controls={<SlidingPills size="sm" options={TIME_OPTIONS} value={timeFilter} onChange={setTimeFilter} ariaLabel="Timeframe" />}
        />
      )}

      {/* Cards replay their rise whenever the tab or timeframe changes */}
      <div key={`${activeTab}-${timeFilter}`} className="space-y-4">
        {activeTab === 'Lost' ? (
          <>
            {overTimeCard}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <PinnableCard id={`${cardPrefix}-reasons`} title="Reasons Lost">
              <GlassBreakdownCard title="Reasons Lost" data={lostByReason} labelKey="reason" emptyText="No lost clients recorded" darkMode={darkMode} delay={140} unit="clients" />
              </PinnableCard>
              <PinnableCard id={`${cardPrefix}-program-stage`} title="Program Stage When Lost">
              <GlassBreakdownCard title="Program Stage When Lost" data={lostByStage} labelKey="stage" emptyText="No stage data available" darkMode={darkMode} delay={200} unit="clients" />
              </PinnableCard>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              {overTimeCard}

              <PinnableCard id={`${cardPrefix}-stage-distribution`} title={`Stage Distribution · ${tabLabel}`}>
              <GlassCard className="flex flex-col p-5" delay={80}>
                <GlassCardHeader title="Stage Distribution" />
                {stageData.length === 0 ? (
                  <GlassEmpty className="h-64">No stage data available</GlassEmpty>
                ) : (
                  <>
                    <div className="my-4 h-52">
                      <GlassDonut data={stageData} dataKey="count" nameKey="stage" inner={74} outer={84} darkMode={darkMode} unit="clients">
                        <div className="text-3xl font-medium text-gray-900 tabular-nums dark:text-white">{stageTotal}</div>
                        <div className="text-xs text-gray-500 dark:text-white/[0.45]">clients</div>
                      </GlassDonut>
                    </div>
                    <div className="grid grid-cols-1 content-start gap-x-6 gap-y-3 sm:grid-cols-2">
                      {stageData.map((entry) => (
                        <div key={entry.stage} className="flex items-center gap-2 text-xs">
                          <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
                          <span className="min-w-0 flex-1 truncate text-gray-700 dark:text-white/75">{entry.stage}</span>
                          <span className="font-semibold text-gray-900 tabular-nums dark:text-white">{entry.count}</span>
                          <span className="w-10 text-right text-gray-500 tabular-nums dark:text-white/40">
                            {stageTotal ? Math.round((entry.count / stageTotal) * 100) : 0}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </GlassCard>
              </PinnableCard>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <PinnableCard id={`${cardPrefix}-revenue`} title={`Revenue · ${tabLabel}`} size="wide">
              <GlassCard className="p-5 xl:col-span-2" delay={140}>
                <GlassCardHeader title="Revenue" />
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <MetricTile label="Total Starting Revenue" value={`$${revenueMetrics.startingRevenue.toLocaleString()}`} />
                  <MetricTile label="Total Current Revenue" value={`$${revenueMetrics.currentRevenue.toLocaleString()}`} tone="mint" />
                  <MetricTile label="Total Revenue Goal" value={`$${revenueMetrics.revenueGoal.toLocaleString()}`} tone="blue" />
                  <MetricTile label="Avg Starting Revenue" value={`$${revenueMetrics.avgStartingRevenue.toLocaleString()}`} />
                  <MetricTile label="Avg Current Revenue" value={`$${revenueMetrics.avgCurrentRevenue.toLocaleString()}`} tone="mint" />
                  <MetricTile label="Avg Revenue Goal" value={`$${revenueMetrics.avgRevenueGoal.toLocaleString()}`} tone="blue" />
                </div>
              </GlassCard>
              </PinnableCard>

              <PinnableCard id={`${cardPrefix}-hours`} title={`Hours · ${tabLabel}`}>
              <GlassCard className="p-5" delay={200}>
                <GlassCardHeader title="Hours" />
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1">
                  <MetricTile label="Total Hour Plans" value={hourMetrics.totalHourPlans} />
                  <MetricTile label="Total Hours Used" value={hourMetrics.totalHoursUsed} />
                </div>
              </GlassCard>
              </PinnableCard>
            </div>

            <PinnableCard id={`${cardPrefix}-department-ratings`} title={`Department Ratings · ${tabLabel}`} size="wide">
            <GlassCard className="p-5" delay={260}>
              <GlassCardHeader title="Department Ratings" />
              <div className="mt-4 space-y-3">
                {RATING_ROWS.map((row, rowIdx) => (
                  <div key={rowIdx} className={`grid grid-cols-1 gap-3 ${row.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
                    {row.map(({ name, key }) => (
                      <RatingTile key={key} name={name} rating={avgStars[key] || 0} />
                    ))}
                  </div>
                ))}
              </div>
            </GlassCard>
            </PinnableCard>
          </>
        )}

        <PinnableCard id={`${cardPrefix}-clients-table`} title={tabLabel} size="wide">
        <GlassCard className="p-5" delay={activeTab === 'Lost' ? 260 : 320}>
          <GlassCardHeader
            title={activeTab === 'Lost' ? 'Lost Clients' : activeTab === 'Active' ? 'Active Clients' : 'All Clients'}
            to="/crm/clients"
            toTitle="Open clients"
            right={<span className={`text-xs ${muted}`}>{filteredClients.length} clients</span>}
          />
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th className={GLASS_TH}>Company Name</th>
                  <th className={GLASS_TH}>Contact Name</th>
                  {activeTab === 'Lost' ? (
                    <>
                      <th className={GLASS_TH}>Reason</th>
                      <th className={GLASS_TH}>Program Lost</th>
                      <th className={GLASS_TH}>Start Date</th>
                      <th className={GLASS_TH}>End Date</th>
                    </>
                  ) : (
                    <>
                      <th className={GLASS_TH}>Status</th>
                      <th className={GLASS_TH}>Program</th>
                      <th className={GLASS_TH}>Created</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="text-gray-700 dark:text-white/75">
                {filteredClients.slice(0, 25).map((c) => {
                  // Get contact name from nested contact object or fallback fields
                  const contactFirst = c.contact?.firstName || '';
                  const contactLast = c.contact?.lastName || '';
                  const contactName = (contactFirst && contactLast)
                    ? `${contactFirst} ${contactLast}`.trim()
                    : c.contactName || c.primaryContactName || c.contact?.name || c.name || '-';

                  // Get status
                  const status = c.status || c.program?.clientStatus || c.stage || 'Unknown';

                  // Get program name
                  const programName = c.program?.programName || c.program?.status || '-';

                  if (activeTab === 'Lost') {
                    const reason = c.resultReason || c.substatus || c.reason || c.lostReason || 'Unknown';
                    const prevStage = c.program?.programName
                      || c.program?.status
                      || c.lastVisitedStage
                      || c.previousStage
                      || (Array.isArray(c.stageHistory) && c.stageHistory.length > 1 ? c.stageHistory[c.stageHistory.length - 2]?.stage : null)
                      || c.stageBeforeLost
                      || c.pipelineStage
                      || 'Unknown';
                    // Get start and end dates from program overview
                    const programStart = c.program?.startDate;
                    const programEnd = c.program?.endDate;
                    const startDate = programStart
                      ? (programStart instanceof Date
                          ? programStart.toLocaleDateString()
                          : new Date(programStart).toLocaleDateString())
                      : '-';
                    const endDate = programEnd
                      ? (programEnd instanceof Date
                          ? programEnd.toLocaleDateString()
                          : new Date(programEnd).toLocaleDateString())
                      : '-';
                    return (
                      <tr key={c.id} className={`${GLASS_ROW} cursor-pointer`} onClick={() => navigate(`/crm/client/${c.id}`)}>
                        <td className="py-3 pr-4">{companyCell(c)}</td>
                        <td className="py-3 pr-4">{contactName}</td>
                        <td className="py-3 pr-4">{reason}</td>
                        <td className="py-3 pr-4">{prevStage}</td>
                        <td className="py-3 pr-4 whitespace-nowrap">{startDate}</td>
                        <td className="py-3 whitespace-nowrap">{endDate}</td>
                      </tr>
                    );
                  }
                  const created = c.createdAt instanceof Date ? c.createdAt.toLocaleDateString() : '-';
                  return (
                    <tr key={c.id} className={`${GLASS_ROW} cursor-pointer`} onClick={() => navigate(`/crm/client/${c.id}`)}>
                      <td className="py-3 pr-4">{companyCell(c)}</td>
                      <td className="py-3 pr-4">{contactName}</td>
                      <td className="py-3 pr-4">
                        <GlassBadge tone={status === 'Lost' || status === 'lost' ? 'red' : 'mint'}>{status}</GlassBadge>
                      </td>
                      <td className="py-3 pr-4">{programName}</td>
                      <td className="py-3 whitespace-nowrap">{created}</td>
                    </tr>
                  );
                })}
                {filteredClients.length === 0 && (
                  <tr>
                    <td colSpan={activeTab === 'Lost' ? 6 : 5} className={`py-8 text-center ${muted}`}>
                      No {activeTab === 'Lost' ? 'lost ' : activeTab === 'Active' ? 'active ' : ''}clients found for selected period
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>
        </PinnableCard>
      </div>
    </>
  );
};

const LostClientsReports = () => {
  const cardMode = useReportCardMode();

  const [activeTab, setActiveTab] = useState('Lost');
  const [loading, setLoading] = useState(true);
  const [allClients, setAllClients] = useState([]);
  const [timeFilter, setTimeFilter] = useState('This Year');
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editClient, setEditClient] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [editStageBeforeLost, setEditStageBeforeLost] = useState('');
  const [editSource, setEditSource] = useState('');
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editContactName, setEditContactName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editPostMortem, setEditPostMortem] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      try {
        const accountsRef = collection(db, 'accounts');
        // Query by type only to avoid composite index issues; filter Lost client-side
        // Try 'Clients' first
        let data = [];
        try {
          const q = query(accountsRef, where('type', '==', 'Clients'), orderBy('createdAt', 'desc'));
          const snapshot = await getDocs(q);
          data = snapshot.docs.map(doc => {
            const d = doc.data();
            return {
              id: doc.id,
              ...d,
              createdAt: d.createdAt?.toDate?.() || (d.createdAt instanceof Date ? d.createdAt : null),
              lostAt: d.lostAt?.toDate?.() || (d.lostAt instanceof Date ? d.lostAt : null),
              lastActivityAt: d.lastActivityAt?.toDate?.() || (d.lastActivityAt instanceof Date ? d.lastActivityAt : null),
              program: d.program ? {
                ...programMapWith(d.program),
                startDate: d.program.startDate?.toDate?.() || (d.program.startDate instanceof Date ? d.program.startDate : d.program.startDate),
                endDate: d.program.endDate?.toDate?.() || (d.program.endDate instanceof Date ? d.program.endDate : d.program.endDate),
              } : d.program,
            };
          });
        } catch (err) {
          console.warn('Failed to query with orderBy, trying without:', err);
          // Fallback: query without orderBy
          const q = query(accountsRef, where('type', '==', 'Clients'));
          const snapshot = await getDocs(q);
          data = snapshot.docs.map(doc => {
            const d = doc.data();
            return {
              id: doc.id,
              ...d,
              createdAt: d.createdAt?.toDate?.() || (d.createdAt instanceof Date ? d.createdAt : null),
              lostAt: d.lostAt?.toDate?.() || (d.lostAt instanceof Date ? d.lostAt : null),
              lastActivityAt: d.lastActivityAt?.toDate?.() || (d.lastActivityAt instanceof Date ? d.lastActivityAt : null),
              program: d.program ? {
                ...programMapWith(d.program),
                startDate: d.program.startDate?.toDate?.() || (d.program.startDate instanceof Date ? d.program.startDate : d.program.startDate),
                endDate: d.program.endDate?.toDate?.() || (d.program.endDate instanceof Date ? d.program.endDate : d.program.endDate),
              } : d.program,
            };
          });
        }
        
        // Also check for lowercase 'clients' type and merge
        let lowercaseData = [];
        try {
          const lowercaseQ = query(accountsRef, where('type', '==', 'clients'), orderBy('createdAt', 'desc'));
          const lowercaseSnapshot = await getDocs(lowercaseQ);
          lowercaseData = lowercaseSnapshot.docs.map(doc => {
            const d = doc.data();
            return {
              id: doc.id,
              ...d,
              createdAt: d.createdAt?.toDate?.() || (d.createdAt instanceof Date ? d.createdAt : null),
              lostAt: d.lostAt?.toDate?.() || (d.lostAt instanceof Date ? d.lostAt : null),
              lastActivityAt: d.lastActivityAt?.toDate?.() || (d.lastActivityAt instanceof Date ? d.lastActivityAt : null),
              program: d.program ? {
                ...programMapWith(d.program),
                startDate: d.program.startDate?.toDate?.() || (d.program.startDate instanceof Date ? d.program.startDate : d.program.startDate),
                endDate: d.program.endDate?.toDate?.() || (d.program.endDate instanceof Date ? d.program.endDate : d.program.endDate),
              } : d.program,
            };
          });
        } catch (err) {
          console.warn('Failed to query lowercase clients with orderBy, trying without:', err);
          // Fallback: query without orderBy
          const lowercaseQ = query(accountsRef, where('type', '==', 'clients'));
          const lowercaseSnapshot = await getDocs(lowercaseQ);
          lowercaseData = lowercaseSnapshot.docs.map(doc => {
            const d = doc.data();
            return {
              id: doc.id,
              ...d,
              createdAt: d.createdAt?.toDate?.() || (d.createdAt instanceof Date ? d.createdAt : null),
              lostAt: d.lostAt?.toDate?.() || (d.lostAt instanceof Date ? d.lostAt : null),
              lastActivityAt: d.lastActivityAt?.toDate?.() || (d.lastActivityAt instanceof Date ? d.lastActivityAt : null),
              program: d.program ? {
                ...programMapWith(d.program),
                startDate: d.program.startDate?.toDate?.() || (d.program.startDate instanceof Date ? d.program.startDate : d.program.startDate),
                endDate: d.program.endDate?.toDate?.() || (d.program.endDate instanceof Date ? d.program.endDate : d.program.endDate),
              } : d.program,
            };
          });
        }
        
        const mergedClients = [...data, ...lowercaseData];
        // Remove duplicates based on id
        const uniqueClients = Array.from(new Map(mergedClients.map(item => [item.id, item])).values());
        // Sort by createdAt descending
        const sortedClients = uniqueClients.sort((a, b) => {
          const aDate = a.createdAt || new Date(0);
          const bDate = b.createdAt || new Date(0);
          return bDate - aDate;
        });
        setAllClients(sortedClients);
      } catch (err) {
        console.error('Failed to fetch clients', err);
        setAllClients([]);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  const openEdit = (client) => {
    setEditClient(client);
    setEditReason(client.resultReason || client.substatus || client.reason || client.lostReason || '');
    setEditSource(client.source || client.channel || client.campaign || '');
    setEditCompanyName(client.company || client.accountName || client.businessName || client.organization || '');
    // Get contact name from nested contact object or fallback fields
    const contactFirst = client.contact?.firstName || '';
    const contactLast = client.contact?.lastName || '';
    const fullContactName = (contactFirst && contactLast) 
      ? `${contactFirst} ${contactLast}`.trim()
      : client.contactName || client.primaryContactName || client.contact?.name || client.name || '';
    setEditContactName(fullContactName);
    setEditEmail(client.contact?.email || client.email || '');
    setEditPhone(client.contact?.phone || client.phone || '');
    setEditNotes(client.lostNotes || client.notes || '');
    setEditPostMortem(Boolean(client.postMortem));
    const prior = client.tierBeforeLost
      || client.program?.programName
      || getProgramTier(client)
      || client.lastVisitedStage
      || client.previousStage
      || (Array.isArray(client.stageHistory) && client.stageHistory.length > 1 ? client.stageHistory[client.stageHistory.length - 2]?.stage : null)
      || client.stageBeforeLost
      || client.pipelineStage
      || '';
    setEditStageBeforeLost(prior);
    setEditError('');
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editClient) return;
    setEditSaving(true);
    setEditError('');
    try {
      const ref = doc(db, 'accounts', editClient.id);
      const updateData = {
        resultReason: editReason || null,
        substatus: editReason || null,
        tierBeforeLost: editStageBeforeLost || null,
        stageBeforeLost: editStageBeforeLost || null,
        lastVisitedStage: editStageBeforeLost || null,
        previousStage: editStageBeforeLost || null,
        source: editSource || null,
        channel: editSource || null,
        company: editCompanyName || null,
        accountName: editCompanyName || null,
        businessName: editCompanyName || null,
        organization: editCompanyName || null,
        contactName: editContactName || null,
        primaryContactName: editContactName || null,
        name: editContactName || null,
        email: editEmail || null,
        phone: editPhone || null,
        lostNotes: editNotes || null,
        notes: editNotes || null,
        postMortem: !!editPostMortem,
      };
      // Update program status if program exists
      if (editClient.program) {
        updateData.program = programMapWith(editClient.program, {
          programName: editStageBeforeLost || editClient.program.programName || null,
          status: editStageBeforeLost || editClient.program.status || null,
        });
      }
      await updateDoc(ref, updateData);

      // Update local state
      setAllClients(prev => prev.map(c => c.id === editClient.id ? {
        ...c,
        resultReason: editReason || null,
        substatus: editReason || null,
        stageBeforeLost: editStageBeforeLost || null,
        lastVisitedStage: editStageBeforeLost || null,
        previousStage: editStageBeforeLost || null,
        source: editSource || null,
        channel: editSource || null,
        company: editCompanyName || null,
        accountName: editCompanyName || null,
        businessName: editCompanyName || null,
        organization: editCompanyName || null,
        contactName: editContactName || null,
        primaryContactName: editContactName || null,
        name: editContactName || null,
        email: editEmail || null,
        phone: editPhone || null,
        lostNotes: editNotes || null,
        notes: editNotes || null,
        postMortem: !!editPostMortem,
        program: c.program ? programMapWith(c.program, {
          programName: editStageBeforeLost || c.program.programName || null,
          status: editStageBeforeLost || c.program.status || null,
        }) : c.program,
      } : c));

      setEditOpen(false);
      setEditClient(null);
    } catch (e) {
      console.error(e);
      setEditError('Failed to save changes.');
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) {
    return (
      <GlassPage backdrop={<ReportBackdrop />}>
        <div className="flex h-64 items-center justify-center"><BBALoader size={96} /></div>
      </GlassPage>
    );
  }

  const muted = 'text-gray-500 dark:text-white/50';
  const viewProps = { setActiveTab, allClients, timeFilter, setTimeFilter };

  return (
    <GlassPage backdrop={<ReportBackdrop />}>
      {cardMode
        ? TAB_OPTIONS.map(({ value }) => <ClientsTabView key={value} activeTab={value} showHeader={false} {...viewProps} />)
        : <ClientsTabView activeTab={activeTab} {...viewProps} />}

      {/* Kept outside the glass cards: backdrop-filter would trap a fixed overlay inside them */}
      {editOpen && (
        <div className={GLASS_MODAL_OVERLAY}>
          <div className="absolute inset-0" onClick={() => !editSaving && setEditOpen(false)} />
          <div className={`${GLASS_MODAL_PANEL} z-10`}>
            <div className="mb-4 text-center">
              <h3 className="text-lg font-medium">Edit Lost Client</h3>
            </div>
            <div className="space-y-4">
              {/* Details overview */}
              <div className={`${GLASS_INSET} p-4`}>
                {editClient && (() => {
                  const created = editClient.createdAt instanceof Date ? editClient.createdAt.toLocaleDateString() : '-';
                  const lost = editClient.lostAt instanceof Date ? editClient.lostAt.toLocaleDateString() : '-';
                  const lastAct = editClient.lastActivityAt instanceof Date ? editClient.lastActivityAt.toLocaleDateString() : '-';
                  const start = editClient.createdAt || editClient.firstSeenAt || editClient.firstContactAt || editClient.clientSince;
                  const end = editClient.lostAt || editClient.lastActivityAt || new Date();
                  const days = (start instanceof Date && end instanceof Date)
                    ? Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)))
                    : '-';
                  const createdBy = editClient.createdByName || editClient.createdBy || editClient.ownerName || editClient.owner || editClient.ownerId || '-';
                  const displaySource = editClient.source || editClient.channel || editClient.campaign || '-';
                  return (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className={muted}>Days As Client</div>
                        <div className="font-medium">{days}</div>
                      </div>
                      <div>
                        <div className={muted}>Created</div>
                        <div className="font-medium">{created}</div>
                      </div>
                      <div>
                        <div className={muted}>Lost</div>
                        <div className="font-medium">{lost}</div>
                      </div>
                      <div>
                        <div className={muted}>Last Activity</div>
                        <div className="font-medium">{lastAct}</div>
                      </div>
                      <div className="col-span-2">
                        <div className={muted}>Created By</div>
                        <div className="font-medium">{createdBy}</div>
                      </div>
                      <div className="col-span-2">
                        <div className={muted}>Source</div>
                        <div className="font-medium">{displaySource}</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm ${muted}`}>Company Name</label>
                  <input type="text" value={editCompanyName} onChange={(e) => setEditCompanyName(e.target.value)} className={GLASS_FIELD} placeholder="Acme Co." disabled={editSaving} />
                </div>
                <div>
                  <label className={`mb-1 block text-sm ${muted}`}>Contact Name</label>
                  <input type="text" value={editContactName} onChange={(e) => setEditContactName(e.target.value)} className={GLASS_FIELD} placeholder="Jane Doe" disabled={editSaving} />
                </div>
                <div>
                  <label className={`mb-1 block text-sm ${muted}`}>Email</label>
                  <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} className={GLASS_FIELD} placeholder="jane@example.com" disabled={editSaving} />
                </div>
                <div>
                  <label className={`mb-1 block text-sm ${muted}`}>Phone</label>
                  <input type="tel" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} className={GLASS_FIELD} placeholder="(555) 555-5555" disabled={editSaving} />
                </div>
              </div>
              <div>
                <label className={`mb-1 block text-sm ${muted}`}>Reason</label>
                <input type="text" value={editReason} onChange={(e) => setEditReason(e.target.value)} className={GLASS_FIELD} placeholder="e.g., Budget constraints" disabled={editSaving} />
              </div>
              <div>
                <label className={`mb-1 block text-sm ${muted}`}>Source</label>
                <input type="text" value={editSource} onChange={(e) => setEditSource(e.target.value)} className={GLASS_FIELD} placeholder="e.g., Website, Referral, Facebook" disabled={editSaving} />
              </div>
              <div>
                <label className={`mb-1 block text-sm ${muted}`}>Program Stage When Lost</label>
                <select value={editStageBeforeLost} onChange={(e) => setEditStageBeforeLost(e.target.value)} className={GLASS_FIELD} disabled={editSaving}>
                  <option value="">Select program</option>
                  <option value="Lite">Lite</option>
                  <option value="Plus">Plus</option>
                  <option value="Pro">Pro</option>
                  <option value="Hourly">Hourly</option>
                  <option value="5 Hours">5 Hours</option>
                  <option value="10 Hours">10 Hours</option>
                  <option value="A La Carte">A La Carte</option>
                </select>
              </div>
              <div>
                <label className={`mb-1 block text-sm ${muted}`}>Notes</label>
                <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={3} className={GLASS_FIELD} placeholder="Add context" disabled={editSaving} />
              </div>
              {editError && (
                <div className="text-sm text-red-500">{editError}</div>
              )}
              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={() => setEditPostMortem(prev => !prev)}
                  disabled={editSaving}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                    editPostMortem
                      ? 'bg-[#F80707] text-white'
                      : 'border border-black/10 text-gray-700 dark:border-white/10 dark:text-white/70'
                  } ${editSaving ? 'opacity-70' : ''}`}
                >
                  Post Mortem
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={GLASS_SECONDARY_BUTTON}
                    onClick={() => setEditOpen(false)}
                    disabled={editSaving}
                  >
                    Cancel
                  </button>
                  <GlassAccentButton onClick={saveEdit} disabled={editSaving}>
                    {editSaving ? 'Saving…' : 'Save'}
                  </GlassAccentButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </GlassPage>
  );
};

export default LostClientsReports;
